/**
 * NX Code Utilities
 * Helper functions for processing NX Python code
 * Based on reference implementation patterns
 *
 * This module must stay import-free. Adding a static import here changes webpack
 * evaluation order in the RagChat / RagLinkAction cycle and throws
 * "Cannot access 'RagLinkAction' before initialization".
 */

export interface NxExecutionEnvelope {
    /** Present when the payload included a numeric status. 0 = success, 1 = failure. */
    status?: 0 | 1;
    message: string;
}

const NX_STATUS_FIELD = /"status"\s*:\s*(\d+)/;
const NX_MESSAGE_FIELD = /"message"\s*:\s*/;

function asNxStatus(value: unknown): 0 | 1 | undefined {
    if (value === undefined || value === null || value === '') return undefined;
    const num = Number(value);
    if (!Number.isFinite(num)) return undefined;
    return num === 0 ? 0 : 1;
}

function nxEnvelopeFromParsedObject(parsed: unknown): NxExecutionEnvelope | null {
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const record = parsed as Record<string, unknown>;
    if (!('status' in record) && !('message' in record)) return null;
    return {
        status: asNxStatus(record.status),
        message: record.message == null ? '' : String(record.message)
    };
}

/**
 * Extract `{ status, message }` from an NX JA payload.
 * Recovers stdout when NX concatenates unescaped `\` / `"` into the JSON envelope.
 */
export function unwrapNxExecutionEnvelope(raw: unknown): NxExecutionEnvelope | null {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) return null;

    try {
        let parsed: unknown = JSON.parse(trimmed);
        if (typeof parsed === 'string') {
            try {
                parsed = JSON.parse(parsed);
            } catch {
                // keep the inner string; it is not a second JSON object
            }
        }
        const envelope = nxEnvelopeFromParsedObject(parsed);
        if (envelope) return envelope;
    } catch {
        // Invalid JSON — fall through to structural extract
    }

    if (!trimmed.startsWith('{')) return null;

    const messageMatch = trimmed.match(NX_MESSAGE_FIELD);
    if (!messageMatch || messageMatch.index === undefined) return null;

    const statusMatch = trimmed.match(NX_STATUS_FIELD);
    let body = trimmed.slice(messageMatch.index + messageMatch[0].length);
    body = body.replace(/\s*\}\s*$/, '');
    if (body.startsWith('"')) {
        body = body.slice(1);
        if (body.endsWith('"')) {
            body = body.slice(0, -1);
        }
    }

    return {
        status: statusMatch ? asNxStatus(statusMatch[1]) : undefined,
        message: body
    };
}

/**
 * Extract Python code blocks from markdown text
 * Supports python, py, and unmarked code blocks
 */
export const extractPythonCode = (text: string): string => {
    // Look for code blocks with python, py, or no language specified
    const codeBlockRegex = /```(?:python|py)\n([\s\S]*?)\n```/g;
    const matches = [];
    let match;

    while ((match = codeBlockRegex.exec(text)) !== null) {
        matches.push(match[1]);
    }

    // Return the last code block found, or empty string if none
    return matches.length > 0 ? matches[matches.length - 1].trim() : '';
};

/**
 * Insert definition functions into code (based on definsert.js reference)
 * This function would typically add common NX imports and helper functions
 */
export const insertDefinitions = (code: string): string => {
    // Check if code already has NXOpen import
    if (code.includes('import NXOpen')) {
        return code;
    }

    // Add standard NXOpen imports and helper functions
    const definitions = `import NXOpen
import NXOpen.UF

_NX_LISTING_WINDOW_LINE_LIMIT = 700

def print(str_msg):
    theSession = NXOpen.Session.GetSession()
    the_lw = theSession.ListingWindow
    the_lw.Open()
    text = "" if str_msg is None else str(str_msg)
    if text == "":
        the_lw.WriteLine("")
        return
    for line in text.splitlines():
        if not line:
            the_lw.WriteLine("")
            continue
        start = 0
        length = len(line)
        while start < length:
            the_lw.WriteLine(line[start:start + _NX_LISTING_WINDOW_LINE_LIMIT])
            start += _NX_LISTING_WINDOW_LINE_LIMIT

`;

    return definitions + code;
};

/**
 * Validate Python code for common NX patterns
 */
export const validateNXCode = (code: string): { isValid: boolean; warnings: string[] } => {
    const warnings: string[] = [];

    // Check for common NX patterns
    if (!code.includes('NXOpen')) {
        warnings.push('Code does not import NXOpen - this may not work in NX');
    }

    if (code.includes('print(') && !code.includes('def print(')) {
        warnings.push('Using print() - consider using NX ListingWindow for output');
    }

    // Check for potentially dangerous operations
    if (code.includes('os.') || code.includes('subprocess') || code.includes('exec(')) {
        warnings.push('Code contains potentially unsafe operations');
    }

    return {
        isValid: warnings.length === 0,
        warnings
    };
};

/**
 * Format code execution result for display.
 * Accepts either the extracted message string, or a raw NX JSON payload
 * `{ "status": 0|1, "message": "..." }` if one is passed through by mistake.
 */
export const formatExecutionResult = (result: string): string => {
    // Remove excessive whitespace
    let formatted = String(result || '').trim();

    const envelope = unwrapNxExecutionEnvelope(formatted);
    if (envelope) {
        formatted = envelope.message.trim();
        if (envelope.status === 0 && !formatted) {
            return '✅ Code executed successfully in NX';
        }
        if (envelope.status === 1 && formatted) {
            return `❌ **Execution failed:**\n\n\`\`\`error\n${formatted}\n\`\`\``;
        }
    }

    // Handle common NX success messages (legacy plain text)
    if (formatted.includes('Execution was successful')) {
        return '✅ Code executed successfully in NX';
    }

    // Handle NX query results - format for better readability
    if (formatted.includes('NX PART QUERY')) {
        // Clean up the formatting for chat display
        formatted = formatted
            .replace(/={50}/g, '') // Remove separator lines
            .replace(/\n\s*\n/g, '\n') // Remove extra blank lines
            .replace(/^\s+/gm, '') // Remove leading spaces
            .trim();

        // Add proper markdown formatting
        formatted = formatted
            .replace(/📋 CURRENT PART INFORMATION/g, '## 📋 Current Part Information')
            .replace(/🔍 BODY ANALYSIS:/g, '### 🔍 Body Analysis')
            .replace(/📊 SUMMARY:/g, '### 📊 Summary')
            .replace(/🏷️  PART ATTRIBUTES/g, '## 🏷️ Part Attributes')
            .replace(/🔧 FEATURE HISTORY/g, '## 🔧 Feature History')
            .replace(/📐 EXPRESSIONS/g, '## 📐 Expressions')
            .replace(/📏 UNITS INFORMATION/g, '## 📏 Units Information');

        return formatted;
    }

    // Handle error messages
    if (formatted.toLowerCase().includes('error') || formatted.toLowerCase().includes('exception')) {
        return `❌ **Execution failed:**\n\n\`\`\`error\n${formatted}\n\`\`\``;
    }

    // Handle general code execution results
    if (formatted.length > 0) {
        // For long results, add code block formatting
        return `**Execution Result:**\n\n\`\`\`execution_result\n${formatted}\n\`\`\``;
    }

    // Return formatted result
    return formatted;
};

/**
 * Normalize an NX execution response for the editable result textarea.
 * NX can return HTML line breaks/entities, while older response formatting can wrap
 * the payload in Markdown headings and fences intended for chat rendering.
 */
export const formatExecutionResultAsPlainText = (result: string): string => {
    let formatted = String(result || '')
        .replace(/&nbsp;|&#160;|&#x0*a0;/gi, ' ')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;|&apos;/gi, "'")
        .replace(/&amp;/gi, '&')
        .replace(/<br\s*\/?\s*>/gi, '\n')
        .trim();

    // Strip chat-only headings/fences first so a prefixed envelope can still unwrap.
    formatted = formatted.replace(
        /^(?:[✅❌]\s*)?\*\*Execution (?:result|failed):\*\*\s*/i,
        ''
    );

    const fencedResult = formatted.match(
        /^```(?:result|execution_result|error)?\s*\n?([\s\S]*?)\n?```$/i
    );
    if (fencedResult) {
        formatted = fencedResult[1].trim();
    }

    const envelope = unwrapNxExecutionEnvelope(formatted);
    if (envelope) {
        formatted = envelope.message.trim();
    }

    const nonEmptyLines = formatted.split('\n').filter(line => line.trim().length > 0);
    if (nonEmptyLines.length > 0 && nonEmptyLines.every(line => /^\s*[*-]\s+/.test(line))) {
        formatted = formatted.replace(/^\s*[*-]\s+/gm, '');
    }

    return formatted
        .split('\n')
        .map(line => line.replace(/[ \t]+$/g, ''))
        .join('\n')
        .trim();
};

/**
 * Generate unique client ID for WebSocket connection
 * Based on ws_connect_server.js reference
 */
export const generateClientId = (prefix = 'nx_client_'): string => {
    return (
        prefix +
        Date.now().toString(36) +          // Millisecond timestamp
        Math.random().toString(36).slice(2) // Random string
    );
};

/**
 * Parse NX response for structured data.
 * Prefers new JSON shape `{ status: 0|1, message }`; falls back to legacy plain text.
 */
export const parseNXResponse = (response: string): {
    type: 'success' | 'error' | 'info';
    message: string;
    data?: any;
} => {
    const trimmed = String(response || '').trim();
    const envelope = unwrapNxExecutionEnvelope(trimmed);
    if (envelope) {
        const status = envelope.status === 0 ? 0 : 1;
        const message = envelope.message;
        return {
            type: status === 0 ? 'success' : 'error',
            message: status === 0
                ? (message || 'Code executed successfully')
                : (message || 'Execution failed'),
            data: message || trimmed
        };
    }
    
    // Legacy success pattern
    if (trimmed.includes('Execution was successful')) {
        return {
            type: 'success',
            message: 'Code executed successfully',
            data: trimmed
        };
    }

    // Check for error patterns
    if (trimmed.toLowerCase().includes('error') ||
        trimmed.toLowerCase().includes('exception') ||
        trimmed.toLowerCase().includes('failed')) {
        return {
            type: 'error',
            message: 'Execution failed',
            data: trimmed
        };
    }

    // Default to info
    return {
        type: 'info',
        message: 'Response received',
        data: trimmed
    };
};

/**
 * Check if code contains NX query functions
 */
export const isNXQueryCode = (code: string): boolean => {
    const queryPatterns = [
        'NXIS_query_by_type(',
        'workPartInfor()',
        'get_part_attributes()',
        'featureList()',
        'printWorkPartExpressions()'
    ];

    return queryPatterns.some(pattern => code.includes(pattern));
};

/**
 * Get default NX query code
 * Based on nxja_query.js askNX function
 */
export const getDefaultNXQueryCode = (): string => {
    return `
from ToolingAutomation import *
NXIS_query_by_type("")
`;
};


/**
 * Add necessary header lines to NXOpen Python code
 * Inserts required imports and initialization code if not already present
 */
export const insertHeader = (code: string): string => {
    // Base imports that are always included
    const baseImports = [
        'import NXOpen',
        'from ToolingAutomation import *',
        'import NXOpen.Assemblies',
        'import NXOpen.Features',
        'import NXOpen.Preferences',
        'import NXOpen.UF',
        'import NXOpen.GeometricUtilities',
        'import json',
        'from nxis import *',
    ];

    // Determine if we should include the undo mark
    // If code includes NXIS_Undo_Last_Step(), don't include the SetUndoMark
    const shouldIncludeUndoMark = !code.includes('NXIS_Undo_Last_Step');

    const requiredLines = shouldIncludeUndoMark
        ? [...baseImports, 'NXOpen.Session.GetSession().SetUndoMark(NXOpen.Session.MarkVisibility.Visible, "NXIS Copilot")']
        : baseImports;

    // Join required lines with newlines and add an extra newline at the end
    const header = requiredLines.join('\n') + '\n\n';

    // Prepend header to the code
    return header + code;
};

export const prepareNxExecutionBridgeRequest = (payload: {
    jaCode: string;
    silent?: unknown;
    suppressCodeDisplay?: unknown;
}): { codeToExecute: string; publishCode: boolean } => {
    const silent = Boolean(payload.silent);
    return {
        codeToExecute: silent ? payload.jaCode : insertHeader(payload.jaCode),
        publishCode: !silent && !Boolean(payload.suppressCodeDisplay),
    };
};

/** Chat-composer policy for NX code execution, NX object deletion, and MCP tools. */
export type NxCodeExecutionPolicy = 'always_ask' | 'always_approve';

/** Which tool the confirmation applies to. Conversation-level allow is tracked per kind. */
export type NxExecutionApprovalKind = 'execute' | 'delete' | 'mcp';

export const NX_CODE_EXECUTION_POLICY_STORAGE_KEY = 'nxCodeExecutionPolicy';

/** Tool-result text when the user declines execute_python_code. */
export const USER_DECLINED_PYTHON_EXECUTION =
    'User declined to execute this Python code. Do not call execute_python_code again with the same or similar code unless the user explicitly asks to proceed.';

/** Tool-result text when the user declines delete_nx_objects. */
export const USER_DECLINED_DELETE_NX_OBJECTS =
    'User declined to delete these NX objects. Do not call delete_nx_objects again with the same tags unless the user explicitly asks to proceed.';

/** Tool-result text when the user declines an MCP tool and no tool name is available. */
export const USER_DECLINED_MCP_TOOL =
    'User declined to run this MCP tool. Do not call this MCP tool again unless the user explicitly asks to proceed.';

export const parseNxExecutionApprovalKind = (value: unknown): NxExecutionApprovalKind => {
    if (value === 'delete' || value === 'mcp') return value;
    return 'execute';
};

export const declinedNxExecutionMessage = (
    kind: NxExecutionApprovalKind,
    toolName?: string
): string => {
    if (kind === 'delete') return USER_DECLINED_DELETE_NX_OBJECTS;
    if (kind === 'mcp') {
        const name = toolName?.trim();
        if (name) {
            return `User declined to run MCP tool ${name}. Do not call ${name} again unless the user explicitly asks to proceed.`;
        }
        return USER_DECLINED_MCP_TOOL;
    }
    return USER_DECLINED_PYTHON_EXECUTION;
};

export const parseNxCodeExecutionPolicy = (value: unknown): NxCodeExecutionPolicy => (
    // New users default to allow; preserve saved choices and ask for invalid settings.
    value == null || value === 'always_approve' ? 'always_approve' : 'always_ask'
);

export const ragChatRequiresExecutionApproval = (payload: { requireApproval?: unknown } | null | undefined): boolean => (
    Boolean(payload?.requireApproval)
);
